import * as React from 'react';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper, { PopperProps } from '@material-ui/core/Popper';
import MenuItemMUI, { MenuItemProps as MenuItemMUIProps } from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import ButtonBase from '@material-ui/core/ButtonBase';
import { makeStyles, createStyles } from '@material-ui/core/styles';
const { useState, useRef, useCallback } = React;

import { Root } from './styled';


export type MenuBaseProps = {
	trigger: React.ReactNode;
	maxHeight?: number;
	children: React.ReactNode;
	PopperProps?: Partial<PopperProps>;
}

const useMenuBaseStyles = makeStyles(() =>
  createStyles({
    trigger: {
			display: 'inline-block',
      maxWidth: '100%',
      padding: 0
    },
  }),
);

const MenuBase:React.SFC<MenuBaseProps> = (props) => {
	const {
		trigger,
		maxHeight,
		PopperProps,
		children,
	} = props;
	const classess = useMenuBaseStyles(props);
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const handleToggle = useCallback(() => setIsOpen(isOpen => !isOpen), []);
  const handleClose = useCallback((e: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(e.target as HTMLElement)) {
      return;
    }

    setIsOpen(false);
	}, [anchorRef]);

  return (
		<Root>
			<ButtonBase
				ref={anchorRef}
				tabIndex={0}
				className={classess.trigger}
				focusRipple
				aria-haspopup='true'
				onClick={handleToggle}>
				{trigger}
			</ButtonBase>
			<Popper
				style={{ zIndex: 1300 }}
				{...PopperProps}
				open={isOpen}
				anchorEl={anchorRef.current}
				transition>
				{({ TransitionProps }) => (
					<Grow
						{...TransitionProps}
						style={{ transformOrigin: '100% 0' }}>
						<Paper
							square
							elevation={4}
							style={{
								minWidth: anchorRef.current ? anchorRef.current.clientWidth : undefined,
								maxHeight: maxHeight,
								overflow: 'auto'
							}}>
							<ClickAwayListener onClickAway={handleClose}>
								{typeof children === 'function' ?	children({ handleClose }) : children}
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</Root>
  );
}

MenuBase.displayName = 'MenuBase';
MenuBase.defaultProps = {
	PopperProps: {
		placement: 'bottom-end',
	},
	maxHeight: 400,
};


type MenuItemProps<T> = {
	readonly?: boolean;
	minWidth?: number;
	component?: any;
} & T & Omit<MenuItemMUIProps, 'button'>

const useMenuItemStyles = makeStyles<any, MenuItemProps<any>, any>(() =>
  createStyles({
    root: ({ readonly, minWidth }) => ({
			minWidth,
			paddingLeft: 24,
			paddingRight: 24,
      '&:hover': {
				backgroundColor: readonly && 'transparent'
			}
    }),
  }),
);

export type MenuItemComponent<T = any> = React.SFC<MenuItemProps<T>>

const MenuItem: MenuItemComponent = (props) => {
	const { readonly, minWidth, className = '', ...rest } = props;
	const classess = useMenuItemStyles(props);

	return (
		<MenuItemMUI
			disableRipple={readonly}
			{...rest}
			className={`${classess.root} ${className}`}
		/>
	);
}

MenuItem.displayName = 'MenuItem';
MenuItem.defaultProps = {};

const Menu = {
	Base: MenuBase,
	List: MenuList,
	Item: MenuItem,
};

export default Menu;
